import React, { useState, useEffect } from 'react';
import { 
  Calendar, Phone, Mail, Users, ThermometerSun, ThermometerSnowflake,
  Plus, Check, Video, MessageSquare, Trash2, ArrowLeft, 
  Linkedin, Facebook, MessageCircle, Twitter, Edit, FileText, 
  X, // Added X for close button
  Briefcase, // Example icon for Meeting
  PhoneCall, // Example icon for Call
  Send // Example icon for Email/Message
} from 'lucide-react';
import { Contact, Interaction, Todo } from '../../types';
import { api } from '../../services/api';
import { getTimeAgo, daysSinceLastInteraction, shouldBeCold } from '../../utils/contactUtils';
import SocialLinks from './SocialLinks';
import RemindersList from '../reminders/RemindersList';
import EditContactModal from './EditContactModal'; // Needed for edit functionality

interface ContactDetailsProps {
  contact: Contact;
  onUpdate: (updatedContact: Contact) => void;
  onClose: () => void;
  onDelete: (contactId: number) => void;
  onTodoChange: () => void;
  existingGroups: string[]; // Pass existingGroups down
}

// Helper to format date consistently
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  } catch (e) {
    return 'Invalid Date';
  }
};

const ContactDetails: React.FC<ContactDetailsProps> = ({
  contact,
  onUpdate,
  onClose,
  onDelete,
  onTodoChange,
  existingGroups
}) => {
  const [newInteraction, setNewInteraction] = useState<{ type: string; notes: string; date: string }>({ type: 'meeting', notes: '', date: new Date().toISOString().split('T')[0] });
  const [newTodo, setNewTodo] = useState({ task: '', dueDate: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [userSettings, setUserSettings] = useState({ coldThresholdDays: 30 });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // General loading state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Make sure the contact data is fresh when opened (optional, depends on app flow)
  useEffect(() => {
    // Reset states when modal opens or contact changes
    setErrorMessage(null);
    setIsLoading(false);
    setNewInteraction({ type: 'meeting', notes: '', date: new Date().toISOString().split('T')[0] });
    setNewTodo({ task: '', dueDate: '' });

    const fetchSettings = async () => {
      try {
        const settings = await api.getUserSettings();
        setUserSettings(settings);
      } catch (error) {
        console.error('Failed to fetch user settings:', error);
      }
    };
    fetchSettings();
  }, [contact]);

  // --- Action Handlers (Delete Contact, Delete Interaction, Add Interaction, Add Todo, Toggle Todo, Delete Todo) ---
  // These functions remain largely the same, just ensure isLoading state is managed

   const handleDeleteContact = async (contactId: number) => {
     if (!window.confirm('Are you sure you want to permanently delete this contact and all associated data (interactions, todos, reminders)? This cannot be undone.')) {
       return;
     }
     setIsLoading(true);
     setIsDeleting(true); // Specific flag for delete button state
     try {
       await api.deleteContact(contactId); // Use the api service method
       onDelete(contactId); // Notify parent
       onClose(); // Close modal
     } catch (error) {
       console.error('Delete error:', error);
       setErrorMessage({ type: 'error', text: 'Failed to delete contact. It might have associated data that needs removal first, or try again later.' });
       setIsLoading(false);
       setIsDeleting(false);
     }
     // No finally needed here as onClose handles success
   };
   
   const deleteInteraction = async (interactionId: number | string) => {
     if (!window.confirm('Are you sure you want to delete this interaction?')) return;
     setIsLoading(true);
     setErrorMessage(null);
     try {
       const idToDelete = typeof interactionId === 'string' ? parseInt(interactionId, 10) : interactionId;
       if (isNaN(idToDelete)) {
         throw new Error("Invalid interaction ID");
       }
       await api.deleteInteraction(idToDelete);
       const updatedInteractions = contact.interactions.filter(i => i.id !== interactionId); 
       const updatedContact = { ...contact, interactions: updatedInteractions };
       onUpdate(updatedContact as Contact); // Explicitly cast to Contact type
     } catch (error) {
       console.error('Failed to delete interaction:', error);
       setErrorMessage({ type: 'error', text: 'Failed to delete interaction.' });
     } finally {
       setIsLoading(false);
     }
   };

   const addInteraction = async () => {
     if (!newInteraction.notes.trim()) {
       setErrorMessage({ type: 'error', text: 'Interaction notes cannot be empty.' });
       return;
     }
     setIsLoading(true);
     setErrorMessage(null);
     try {
       const interactionData = { type: newInteraction.type, date: newInteraction.date, notes: newInteraction.notes };
       const createdInteraction = await api.createInteraction(contact.id, interactionData);
       const isNewerInteraction = new Date(createdInteraction.date) > new Date(contact.lastInteraction);
       const wasCold = contact.warmth === 'cold';
       const updatedContact = {
         ...contact,
         interactions: [createdInteraction, ...contact.interactions],
         lastInteraction: isNewerInteraction ? createdInteraction.date : contact.lastInteraction,
         warmth: wasCold ? 'warm' : contact.warmth
       };
       onUpdate(updatedContact);
       setNewInteraction({ type: 'meeting', notes: '', date: new Date().toISOString().split('T')[0] });
       if (wasCold) {
         setErrorMessage({ type: 'success', text: 'Contact marked as warm due to new interaction!' });
       }
     } catch (error) {
       console.error('Failed to add interaction:', error);
       setErrorMessage({ type: 'error', text: 'Failed to add interaction.' });
     } finally {
       setIsLoading(false);
     }
   };

   const addTodo = async () => {
     if (!newTodo.task.trim()) return;
     setIsLoading(true);
     setErrorMessage(null);
     try {
       console.log('[ContactDetails.addTodo] Attempting to add todo:', { task: newTodo.task, dueDate: newTodo.dueDate }); // LOG: Log attempt
       
       // Log the contact ID being used *immediately* before the API call
       console.log(`[ContactDetails.addTodo] Using contact.id: ${contact.id} for api.createTodo`); 
       
       const todoData = { task: newTodo.task, dueDate: newTodo.dueDate || undefined, completed: false };
       const createdTodo = await api.createTodo(contact.id, todoData);
       console.log('[ContactDetails.addTodo] Received createdTodo from API:', createdTodo);

       // Call parent handler to refetch
       onTodoChange();

       setNewTodo({ task: '', dueDate: '' });
     } catch (error) {
       console.error('[ContactDetails.addTodo] Failed to add todo:', error); // LOG: Log error
       setErrorMessage({ type: 'error', text: 'Failed to add todo.' });
     } finally {
       setIsLoading(false);
     }
   };

   const toggleTodo = async (todoId: string) => {
     const todo = contact.todos.find(t => t.id === todoId);
     if (!todo) return;
     setIsLoading(true);
     setErrorMessage(null);
     try {
       const updatedTodoData = { ...todo, completed: !todo.completed };
       const updatedTodo = await api.updateTodo(todoId, updatedTodoData);
       onTodoChange();
     } catch (error) {
       console.error('Failed to update todo status:', error);
       setErrorMessage({ type: 'error', text: 'Failed to update todo.' });
     } finally {
       setIsLoading(false);
     }
   };

   const deleteTodo = async (todoId: string) => {
     if (!window.confirm('Are you sure you want to delete this TODO?')) return;
     setIsLoading(true);
     setErrorMessage(null);
     try {
       await api.deleteTodo(todoId);
       onTodoChange();
     } catch (error) {
       console.error('Failed to delete todo:', error);
       setErrorMessage({ type: 'error', text: 'Failed to delete todo.' });
     } finally {
       setIsLoading(false);
     }
   };
  // --- End Action Handlers ---

  const handleUpdateContactFromEdit = (updatedContactData: Contact) => {
     onUpdate(updatedContactData); // Update the parent state
     // Optionally, update local state if needed, though closing usually handles this
   };

  const isCold = shouldBeCold(contact, userSettings.coldThresholdDays);
  const warmthDisplay = contact.warmth === 'cold' || isCold ? 'cold' : 'warm';

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'meeting': return <Briefcase size={16} className="text-purple-600" />;
      case 'call': return <PhoneCall size={16} className="text-blue-600" />;
      case 'email':
      case 'message': return <Send size={16} className="text-green-600" />;
      default: return <MessageSquare size={16} className="text-gray-500" />;
    }
  };

  return (
    <>
      {/* Modal Overlay & Panel Structure */}
      <div 
        className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-start justify-center z-40 p-4 transition-opacity duration-300 ease-out overflow-y-auto pt-12 md:pt-20" // Added padding-top
        onClick={onClose} // Close when clicking overlay
      >
        <div 
          className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden my-8" // Increased max-width
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
        >
          {/* Header Style */}
          <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0 bg-white sticky top-0 z-10">
            <div className="flex items-center gap-3 min-w-0">
               {/* Warmth Indicator */}
               <span className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${warmthDisplay === 'warm' ? 'bg-tag-yellow-bg text-tag-yellow-text' : 'bg-tag-blue-bg text-tag-blue-text'}`}>
                 {warmthDisplay === 'warm' ? <ThermometerSun size={14} className="mr-1.5"/> : <ThermometerSnowflake size={14} className="mr-1.5"/>}
                 {warmthDisplay === 'warm' ? 'Warm' : 'Cold'}
               </span>
               <h2 className="text-lg font-semibold text-gray-800 truncate" title={contact.name}>{contact.name}</h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Edit Button: Secondary Style */}
              <button 
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center gap-1.5 bg-white text-gray-700 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
                disabled={isLoading}
              >
                <Edit size={16} /> Edit
              </button>
              <button 
                onClick={onClose} 
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={isLoading}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
            {/* Optional Success/Error Message Area */}
            {errorMessage && (
              <div className={`mb-4 p-3 rounded-lg border text-sm ${errorMessage.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                {errorMessage.text}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left Column: Contact Info & Details */}
              <div className="md:col-span-1 space-y-5">
                {/* Contact Info Section */}
                <div className="space-y-2">
                   <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Info</h3>
                   {contact.email && <div className="flex items-center gap-2 text-sm text-gray-700"><Mail size={14} className="text-gray-400"/> <a href={`mailto:${contact.email}`} className="hover:underline hover:text-brand-primary truncate" title={contact.email}>{contact.email}</a></div>}
                   {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-700"><Phone size={14} className="text-gray-400"/> <a href={`tel:${contact.phone}`} className="hover:underline hover:text-brand-primary truncate">{contact.phone}</a></div>}
                   {contact.birthday && <div className="flex items-center gap-2 text-sm text-gray-700"><Calendar size={14} className="text-gray-400"/> Born {formatDate(contact.birthday)}</div>}
                   <div className="flex items-center gap-2 text-sm text-gray-700"><Calendar size={14} className="text-gray-400"/> Last interaction: {getTimeAgo(contact.lastInteraction)}</div>
                </div>

                {/* Groups Section */}
                 <div>
                   <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Groups</h3>
                   <div className="flex flex-wrap gap-1.5">
                     {contact.groups.length > 0 ? contact.groups.map(group => (
                       <span key={group} className="bg-tag-blue-bg text-tag-blue-text px-2 py-0.5 rounded-full text-xs font-medium">
                         {group}
                       </span>
                     )) : <span className="text-sm text-gray-500 italic">No groups</span>}
                   </div>
                 </div>

                 {/* Social Links Section */}
                 {(contact.linkedin || contact.facebook || contact.telegram || contact.xcom) && (
                   <div>
                     <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Social Profiles</h3>
                     <SocialLinks 
                       linkedin={contact.linkedin}
                       facebook={contact.facebook}
                       telegram={contact.telegram}
                       xcom={contact.xcom}
                       onUpdate={(socialUpdates) => {
                          // Merge the socialUpdates into the existing contact data
                          const updatedContactData = { 
                            ...contact, // Start with the current contact data
                            ...socialUpdates // Overwrite with the new social media links
                          };
                          // Call the main onUpdate handler passed to ContactDetails with the complete updated contact
                          onUpdate(updatedContactData);
                        }}
                     />
                   </div>
                 )}

                 {/* Notes Section */}
                 {contact.notes && (
                   <div>
                     <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><FileText size={14}/> Notes</h3>
                     <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-md border border-gray-200">
                       {contact.notes}
                     </div>
                   </div>
                 )}
              </div>

              {/* Right Column: Interactions, Todos, Reminders */}
              <div className="md:col-span-2 space-y-6">
                {/* Add Interaction Section */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                   <h3 className="text-sm font-semibold text-gray-800 mb-3">Log New Interaction</h3>
                   <div className="flex flex-col sm:flex-row gap-2 mb-2">
                      {/* Interaction Type Buttons: Filter/Tag Style */}
                      <div className="flex gap-1 flex-wrap">
                        {(['meeting', 'call', 'email', 'message'] as const).map(type => (
                           <button 
                             key={type}
                             type="button"
                             onClick={() => setNewInteraction(prev => ({ ...prev, type }))}
                             className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150 border capitalize ${newInteraction.type === type ? 'bg-blue-100 text-blue-700 border-blue-300 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'}`}
                             disabled={isLoading}
                           >
                             {getInteractionIcon(type)} {type}
                           </button>
                        ))}
                      </div>
                      <input
                        type="date"
                        className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                        value={newInteraction.date}
                        onChange={(e) => setNewInteraction(prev => ({ ...prev, date: e.target.value }))}
                        disabled={isLoading}
                      />
                   </div>
                   <textarea
                     rows={2}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-white text-sm mb-2"
                     placeholder="Notes about the interaction..."
                     value={newInteraction.notes}
                     onChange={(e) => setNewInteraction(prev => ({ ...prev, notes: e.target.value }))}
                     disabled={isLoading}
                   ></textarea>
                   <button 
                     onClick={addInteraction} 
                     className="w-full sm:w-auto bg-brand-primary text-white px-4 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                     disabled={isLoading || !newInteraction.notes.trim()}
                   >
                     {isLoading ? 'Adding...' : 'Add Interaction'}
                   </button>
                </div>

                {/* Interactions History Section */}
                <div>
                   <h3 className="text-sm font-semibold text-gray-800 mb-3">Interaction History</h3>
                   <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                     {contact.interactions.length > 0 ? contact.interactions.map(interaction => (
                       <div key={interaction.id} className="flex items-start gap-3 group">
                         <div className="mt-1 flex-shrink-0">{getInteractionIcon(interaction.type)}</div>
                         <div className="flex-1">
                           <div className="flex justify-between items-center">
                              <span className="text-xs font-medium text-gray-500">{formatDate(interaction.date)}</span>
                              <button 
                                 onClick={() => deleteInteraction(interaction.id)} 
                                 className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                 title="Delete Interaction"
                                 disabled={isLoading}
                              >
                                <Trash2 size={14} />
                              </button>
                           </div>
                           <p className="text-sm text-gray-700 whitespace-pre-wrap">{interaction.notes}</p>
                         </div>
                       </div>
                     )) : <p className="text-sm text-gray-500 italic">No interactions logged yet.</p>}
                   </div>
                 </div>

                 {/* TODOs Section */}
                 <div>
                   <h3 className="text-sm font-semibold text-gray-800 mb-3">TODOs</h3>
                   {/* Add TODO Form */}
                   <div className="flex gap-2 mb-3">
                      <input 
                        type="text" 
                        placeholder="New TODO task..." 
                        value={newTodo.task}
                        onChange={(e) => setNewTodo(prev => ({ ...prev, task: e.target.value }))}
                        className="flex-grow px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                        disabled={isLoading}
                      />
                      <input 
                        type="date"
                        value={newTodo.dueDate}
                        onChange={(e) => setNewTodo(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                        disabled={isLoading}
                      />
                      <button 
                        onClick={addTodo} 
                        className="bg-brand-primary text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50"
                        disabled={isLoading || !newTodo.task.trim()}
                      >
                        Add
                      </button>
                   </div>
                   {/* TODO List */}
                   <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                     {contact.todos.length > 0 ? contact.todos.map(todo => (
                       <div key={todo.id} className={`flex items-center gap-2 group p-1.5 rounded ${todo.completed ? 'opacity-60' : 'hover:bg-gray-50'}`}>
                          <button 
                            onClick={() => toggleTodo(todo.id)} 
                            className={`w-5 h-5 border-2 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${todo.completed ? 'bg-blue-500 border-blue-500' : 'border-gray-300 hover:border-blue-400'}`}
                            disabled={isLoading}
                          >
                            {todo.completed && <Check size={14} className="text-white" />}
                          </button>
                          <span className={`flex-1 text-sm ${todo.completed ? 'line-through text-gray-500' : 'text-gray-800'}`}>{todo.task}</span>
                          {todo.dueDate && <span className="text-xs text-gray-500">Due: {formatDate(todo.dueDate)}</span>}
                          <button 
                             onClick={() => deleteTodo(todo.id)}
                             className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                             title="Delete TODO"
                             disabled={isLoading}
                          >
                             <Trash2 size={14} />
                          </button>
                       </div>
                     )) : <p className="text-sm text-gray-500 italic">No TODOs for this contact.</p>}
                   </div>
                 </div>

                 {/* Reminders Section */}
                 <RemindersList 
                    contact={contact} // Pass the full contact object
                    // onUpdateContact={onUpdate} // Optionally pass if RemindersList needs to update parent directly
                 />

              </div>
            </div>
          </div>

          {/* Footer for Delete Action */}
          <div className="flex justify-end items-center gap-3 p-4 border-t border-gray-200 flex-shrink-0">
             {/* Delete Button: Destructive Style */}
             <button
               type="button"
               onClick={() => setShowDeleteConfirm(true)}
               className={`bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-sm ${
                 isDeleting || isLoading ? 'opacity-50 cursor-not-allowed' : '' // Use isDeleting for specific visual state
               }`}
               disabled={isDeleting || isLoading}
             >
               {isDeleting ? 'Deleting...' : 'Delete Contact'}
             </button>
          </div>

          {/* Confirmation section */}
          {showDeleteConfirm && (
            <div className="mt-4 p-4 rounded-lg border border-gray-200 flex items-center justify-between gap-4"> 
              {/* Removed background/border styles */}
              <div className="flex gap-3">
                <button
                  onClick={() => handleDeleteContact(contact.id)}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium shadow-sm"
                >
                  Confirm Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Edit Contact Modal (uses the already styled component) */}
      {isEditModalOpen && (
        <EditContactModal
          contact={contact}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdate={(updatedData) => {
            handleUpdateContactFromEdit(updatedData); // Propagate update
            setIsEditModalOpen(false); // Close after successful update
          }}
          existingGroups={existingGroups}
        />
      )}
    </>
  );
};

export default ContactDetails;